// @ts-nocheck
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import {
  Box,
  Button,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  useLazyGetRolesForPageQuery,
  useGetRolesToUserMutation,
  useAssignRoleToUserMutation,
  useFetchDataQuery,
  useUpdateCompanyUserStatusMutation,
  useDeActiveCompanyUserMutation,
} from '../../../../components/services/services';
import OptimizedCreateUserModal from './OptimizedCreateUserModal';
import RoleManagementModal from './RoleManagementModal';
import UpdatePhoneOrEmailModal from './UpdatePhoneOrEmailModal';
import Table from '../../../../components/table/Table';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { RootState } from '../../../../store/store';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { enqueueSnackbar } from 'notistack';
import { filterOperatorsDateTime } from '../../../../utils/filter-date-time';
import { Warning } from '@mui/icons-material';
import { ConfirmationModal } from '../../../../components/ConfirmationModal';
import { usePermission } from '../../../../hooks/usePermission';
import { useRefreshCurrentUser } from '../../../../hooks/useRefreshCurrentUser';

interface UsersListProps {
  isLoadingPermissions: boolean;
  canViewUserList: boolean;
  userModalOpen: boolean;
  setUserModalOpen: (open: boolean) => void;
}

const UsersList: React.FC<UsersListProps> = ({
  isLoadingPermissions,
  canViewUserList,
  userModalOpen,
  setUserModalOpen,
}) => {
  const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
  const { refreshCurrentUserPreserveSelection } = useRefreshCurrentUser();

  const [getRolesForPageTrigger, { data: rolesData, isLoading: rolesLoading }] = useLazyGetRolesForPageQuery();

  const [getRolesToUser] = useGetRolesToUserMutation();
  const [assignRoleToUser] = useAssignRoleToUserMutation();
  const [updateCompanyUserStatus] = useUpdateCompanyUserStatusMutation();
  const [deActiveCompanyUser] = useDeActiveCompanyUserMutation();

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>({
    id: false,
  });
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [userCurrentRoles, setUserCurrentRoles] = useState<string[]>([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleSuccess, setRoleSuccess] = useState(false);
  const [currentRoleLoading, setCurrentRoleLoading] = useState(false);

  // Aktif/Pasif işlemi için state'ler
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedUserForStatus, setSelectedUserForStatus] = useState<any>(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  // Silme işlemi için state'ler
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState<any>(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  // E-mail / Cep No güncelleme modal
  const [updatePhoneOrEmailModalOpen, setUpdatePhoneOrEmailModalOpen] = useState(false);
  const [updatePhoneOrEmailUser, setUpdatePhoneOrEmailUser] = useState<{ customerId: number; email: string; phoneNumber: string } | null>(null);
  const [updatePhoneOrEmailType, setUpdatePhoneOrEmailType] = useState<'email' | 'phone'>('email');

  // İşlemler menüsü (hangi satırın menüsü açık)
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionsMenuRow, setActionsMenuRow] = useState<any>(null);
  const openActionsMenu = (event: React.MouseEvent<HTMLElement>, row: any) => {
    event.stopPropagation();
    setActionsMenuAnchor(event.currentTarget);
    setActionsMenuRow(row);
  };
  const closeActionsMenu = () => {
    setActionsMenuAnchor(null);
    setActionsMenuRow(null);
  };

  const getColumns = (showActionsColumn: boolean): GridColDef[] => {
    const baseColumns: GridColDef[] = [
      { field: 'id', headerName: 'ID', width: 90 },
      {
        field: 'cDate',
        headerName: 'Oluşturma Tarihi',
        width: 180,
        type: 'datetime',
        filterOperators: filterOperatorsDateTime,
      },
      { field: 'userName', headerName: 'Kullanıcı Adı', width: 200 },
      { field: 'email', headerName: 'Email', width: 250 },
      { field: 'phoneNumber', headerName: 'Telefon', width: 150 },
      { field: 'company', headerName: 'Şirket', width: 200 },
      {
        field: 'isActive', 
        headerName: 'Durum',
        width: 120,
        type: 'singleSelect',
        filterable: true,
        sortable: true,
        valueOptions: [
          { value: 'true', label: 'Aktif' },
          { value: 'false', label: 'Pasif' },
        ],
        renderCell: (params: GridRenderCellParams) => (
          <Typography
            sx={{
              color: params?.row?.isActive ? 'green !important' : 'red !important',
              fontWeight: 'bold',
            }}
          >
            {params?.row?.isActive ? 'Aktif' : 'Pasif'}
          </Typography>
        ),
      },
    ];

    if (showActionsColumn) {
      baseColumns.push({
        field: 'actions',
        headerName: 'İşlemler',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Button
            variant="outlined"
            size="small"
            endIcon={<MoreVertIcon />}
            onClick={(e) => openActionsMenu(e, params.row)}
            sx={{
              minWidth: 'auto',
              padding: '4px 10px',
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: 1,
              border: '1px solid',
              borderColor: '#64748b',
              color: '#475569',
              background: '#fff',
              height: '32px',
              '&:hover': {
                borderColor: '#1e3c72',
                color: '#1e3c72',
                background: 'rgba(30, 60, 114, 0.04)',
              },
            }}
          >
            İşlemler
          </Button>
        ),
      });
    }

    return baseColumns;
  };

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const listUrl = `/User/GetUsersV2`;
  const [requestBody, setRequestBody] = useState({
    filters: [],
    pageSize: 25,
    page: 1,
    orderBy: '',
    orderDesc: false,
    companyId: currentUserState?.companyId || undefined,
  });

  const {
    data: fetchData,
    isFetching,
    refetch: refetchUsers,
  } = useFetchDataQuery(
    { url: listUrl, filter: requestBody, method: 'POST' },
    { skip: !requestBody?.companyId || isLoadingPermissions || !canViewUserList }
  );

  // Kurum değişince requestBody.companyId güncellenmezse kullanıcı listesi refetch olmayabilir
  useEffect(() => {
    if (!currentUserState?.companyId) return;
    setPaginationModel({ page: 0, pageSize: 25 });
    setRequestBody((prev) => ({
      ...prev,
      filters: [],
      pageSize: 25,
      page: 1,
      orderBy: '',
      orderDesc: false,
      companyId: currentUserState.companyId,
    }));
  }, [currentUserState?.companyId]);

  // Rol atama yetkisi kontrolü - liste yüklendikten sonra kontrol edilecek
  // Liste yüklendiğinde (fetchData mevcut olduğunda) permission kontrolü yapılacak
  const { hasPermission: canAssignRole, isLoading: isRolePermissionLoading } = usePermission({
    code: 'Corporate.POST.Writing.KullanıcıyaRolAtama',
    enabled: !!fetchData && !isFetching && !!requestBody?.companyId // Liste yüklendikten sonra kontrol et
  });

  // E-mail / Cep No güncelleme yetkisi
  const { hasPermission: canUpdateUserInfo, isLoading: isUpdateUserInfoPermissionLoading } = usePermission({
    code: 'Corporate.POST.Updating.KullanıcıBilgileriGüncelleme',
    enabled: !!fetchData && !isFetching && !!requestBody?.companyId
  });

  // Kullanıcı durumu (aktif/pasif) güncelleme yetkisi
  const { hasPermission: canUpdateUserStatus, isLoading: isUpdateUserStatusPermissionLoading } = usePermission({
    code: 'Corporate.POST.Updating.KullanıcıDurumGüncellemeYetkisi',
    enabled: !!fetchData && !isFetching && !!requestBody?.companyId
  });

  // Kullanıcı silme yetkisi
  const { hasPermission: canDeleteUser, isLoading: isDeleteUserPermissionLoading } = usePermission({
    code: 'Corporate.POST.Reading.KullanıcıSilmeYetkisi',
    enabled: !!fetchData && !isFetching && !!requestBody?.companyId
  });

  const showActionsColumn = canUpdateUserStatus || canAssignRole || canUpdateUserInfo || canDeleteUser;

  // columns'ı useMemo ile memoize et - her render'da yeni referans oluşturmasın
  // canAssignRole tanımlandıktan sonra çağrılmalı
  const columns = useMemo(() => getColumns(showActionsColumn), [showActionsColumn]);

  // Optimize edilmiş modal için gereken callback'ler
  const handleUserCreated = async () => {
    setPaginationModel({ page: 0, pageSize: 25 });
    await refetchUsers();

    if (
      updatePhoneOrEmailType === 'email' &&
      updatePhoneOrEmailUser &&
      currentUserState?.customerId &&
      Number(updatePhoneOrEmailUser.customerId) === Number(currentUserState.customerId)
    ) {
      await refreshCurrentUserPreserveSelection();
    }
  };
  const sortModelChange = (model: any) => {
    setRequestBody((prev) => ({ ...prev, orderBy: model[0]?.field ?? '', orderDesc: model[0]?.sort === 'asc' }));
  };

  // Debounce için timeout ref'i
  const filterTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // useCallback ile memoize edilmiş filter change handler
  const onFilterChange = useCallback((filterObj: any) => {
    // Önceki timeout'u temizle
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }
    
    // Yeni timeout ayarla
    filterTimeoutRef.current = setTimeout(() => {
      setRequestBody((prev) => ({ ...prev, filters: filterObj }));
    }, 500);
  }, []); // Boş dependency array - sadece bir kez oluşturulacak

  // Cleanup: Component unmount olduğunda timeout'u temizle
  useEffect(() => {
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, []);

  const handleChangePagination = (model: any) => {
    setPaginationModel(model);
    setRequestBody((prev) => ({ ...prev, pageSize: model.pageSize, page: model.page + 1 }));
  };

  // Role name'den sadece son kısmı al (CorporateCompany.3284.Uzman -> Uzman)
  const extractRoleName = (fullRoleName: string) => {
    const parts = fullRoleName.split('.');
    return parts[parts.length - 1] || fullRoleName;
  };

  const handleRoleButtonClick = async (user: any) => {
    setSelectedUser(user);
    setSelectedRole('');
    setUserCurrentRoles([]);
    setRoleModalOpen(true);
    setCurrentRoleLoading(true);

    try {
      if (currentUserState?.companyId) {
        const rolesResult = await getRolesForPageTrigger({ companyId: currentUserState.companyId });
        const userRolesResult = await getRolesToUser({ userId: user.id });
        if (userRolesResult.data && userRolesResult.data.success && rolesResult.data) {
          const currentUserRoleNames = userRolesResult.data.data || [];
          const availableRoles = rolesResult.data.data || [];

          // İlk mevcut rolü seç (tek rol olduğu için)
          if (Array.isArray(currentUserRoleNames) && currentUserRoleNames.length > 0) {
            const firstRole = availableRoles.find((role) =>
              currentUserRoleNames.some((userRole) => userRole === role.name)
            );
            if (firstRole) {
              setSelectedRole(firstRole.name);
            }
          }

          setUserCurrentRoles(
            Array.isArray(currentUserRoleNames)
              ? currentUserRoleNames.map((role: any) => (typeof role === 'string' ? role : role.name ?? String(role)))
              : []
          );
        }
      }
    } catch (error) {
      enqueueSnackbar('Roller alınırken bir hata oluştu!', { variant: 'error' });
    } finally {
      setCurrentRoleLoading(false);
    }
  };

  const handleRoleAssign = async () => {
    if (!selectedUser || !selectedRole) return;

    // Rol atama yetkisi kontrolü
    if (!canAssignRole) {
      enqueueSnackbar('Rol atama yetkiniz bulunmamaktadır!', { variant: 'error' });
      return;
    }

    setRoleLoading(true);
    try {
      const result = await assignRoleToUser({
        userId: selectedUser.id,
        roles: [selectedRole],
      });

      if (result.data && result.data.success) {
        // Başarı animasyonu göster
        setRoleSuccess(true);
        setRoleLoading(false);

        // 2 saniye sonra modal'ı kapat
        setTimeout(() => {
          setRoleModalOpen(false);
          setSelectedUser(null);
          setSelectedRole('');
          setUserCurrentRoles([]);
          setRoleSuccess(false);
          enqueueSnackbar('Rol başarıyla atandı!', { variant: 'success' });
        }, 2000);
      }
    } catch (error) {
      enqueueSnackbar('Rol atanırken bir hata oluştu!', { variant: 'error' });
      setRoleLoading(false);
    }
  };

  const handleRoleModalClose = () => {
    setRoleModalOpen(false);
    setSelectedUser(null);
    setSelectedRole('');
    setUserCurrentRoles([]);
    setRoleSuccess(false);
    setCurrentRoleLoading(false);
  };

  // Aktif/Pasif buton tıklama işlemi
  const handleStatusButtonClick = (user: any) => {
    setSelectedUserForStatus(user);
    setConfirmDialogOpen(true);
  };

  // Kullanıcı durumu güncelleme işlemi
  const handleStatusUpdate = async () => {
    if (!selectedUserForStatus) return;

    setStatusUpdateLoading(true);
    try {
      const newStatus = !selectedUserForStatus.isActive;

      const result = await updateCompanyUserStatus({
        companyId: currentUserState?.companyId || 0,
        customerId: selectedUserForStatus.customerId,
        isActive: newStatus,
      });

      if (result.data && result.data.success) {
        enqueueSnackbar(`Kullanıcı başarıyla ${newStatus ? 'aktif' : 'pasif'} edildi!`, { variant: 'success' });
        await refetchUsers();
      } else {
        enqueueSnackbar('Kullanıcı durumu güncellenirken bir hata oluştu!', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Kullanıcı durumu güncellenirken bir hata oluştu!', { variant: 'error' });
    } finally {
      setStatusUpdateLoading(false);
      setConfirmDialogOpen(false);
      setSelectedUserForStatus(null);
    }
  };

  // Onay dialog'unu kapatma
  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
    setSelectedUserForStatus(null);
  };

  // Sil buton tıklama işlemi
  const handleDeleteButtonClick = (user: any) => {
    setSelectedUserForDelete(user);
    setDeleteConfirmDialogOpen(true);
  };

  // Kullanıcı silme işlemi
  const handleDeleteUser = async () => {
    if (!selectedUserForDelete) return;
    if (!canDeleteUser) {
      enqueueSnackbar('Kullanıcı silme yetkiniz bulunmamaktadır!', { variant: 'error' });
      setDeleteConfirmDialogOpen(false);
      setSelectedUserForDelete(null);
      return;
    }

    setDeleteUserLoading(true);
    try {
      const result = await deActiveCompanyUser({
        companyId: currentUserState?.companyId || 0,
        customerId: selectedUserForDelete.customerId,
        makingCustomerId: currentUserState?.companyId || 0,
      });

      if (result.data && result.data.success) {
        enqueueSnackbar('Kullanıcı başarıyla silindi!', { variant: 'success' });
        await refetchUsers();
      } else {
        enqueueSnackbar('Kullanıcı silinirken bir hata oluştu!', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Kullanıcı silinirken bir hata oluştu!', { variant: 'error' });
    } finally {
      setDeleteUserLoading(false);
      setDeleteConfirmDialogOpen(false);
      setSelectedUserForDelete(null);
    }
  };

  // Silme onay dialog'unu kapatma
  const handleDeleteConfirmDialogClose = () => {
    setDeleteConfirmDialogOpen(false);
    setSelectedUserForDelete(null);
  };

  return (
    <div>
      <Table
        showDownloadButton={true}
        sortModelChange={sortModelChange}
        initialState={
          {
            pinnedColumns: {
              left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
              right: showActionsColumn ? ['actions'] : [],
            },
          } as GridInitialStatePro
        }
        sx={{
          margin: '0px !important',
        }}
        columnVisibilityConfigKey="UsersTableFilterColumnVisibilityKey"
        filterKey="UsersTableFilter"
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        checkboxSelection
        isMasterDetailActive={false}
        headerFilters={true}
        rows={fetchData?.data ?? []}
        columns={columns}
        isLoading={
          isFetching ||
          isLoadingPermissions ||
          isRolePermissionLoading ||
          isUpdateUserInfoPermissionLoading ||
          isUpdateUserStatusPermissionLoading ||
          isDeleteUserPermissionLoading
        }
        handleFilterChange={onFilterChange}
        paginationModel={paginationModel}
        onPaginationModelChange={handleChangePagination}
        rowCount={fetchData?.totalCount ?? 0}
        getRowId={(row: any) => row.id}
        idName={'id'}
        listUrl={listUrl}
        fetchData={fetchData}
        listName="kullanci-listesi"
        requestBody={requestBody}
        fetchMethod="POST"
      />

      {/* İşlemler menüsü - tablo dışında, böylece dropdown görünür */}
      <Menu
        anchorEl={actionsMenuAnchor}
        open={Boolean(actionsMenuAnchor)}
        onClose={closeActionsMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            minWidth: 220,
            py: 0.5,
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiMenuItem-root': {
              py: 0.5,
              px: 1.5,
              fontSize: '0.875rem',
              borderRadius: 1,
              mx: 0.5,
              my: 0,
              minHeight: 'auto',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            },
            '& .MuiListItemIcon-root': {
              minWidth: 36,
              color: 'inherit',
            },
          },
        }}
        MenuListProps={{ sx: { py: 0.5 } }}
      >
        {canUpdateUserStatus && (
          <>
            <MenuItem
              onClick={() => {
                if (actionsMenuRow) handleStatusButtonClick(actionsMenuRow);
                closeActionsMenu();
              }}
              disabled={statusUpdateLoading}
              sx={{ color: actionsMenuRow?.isActive ? '#d97706' : '#059669' }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                {actionsMenuRow?.isActive ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={actionsMenuRow?.isActive ? 'Pasif Et' : 'Aktif Et'} primaryTypographyProps={{ fontWeight: 500 }} />
            </MenuItem>
            <Divider sx={{ my: 0.25 }} />
          </>
        )}
        {canAssignRole && (
          <>
            <MenuItem
              onClick={() => {
                if (actionsMenuRow) handleRoleButtonClick(actionsMenuRow);
                closeActionsMenu();
              }}
              sx={{ color: '#059669' }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}><AddIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Rol Yönet" primaryTypographyProps={{ fontWeight: 500 }} />
            </MenuItem>
            <Divider sx={{ my: 0.25 }} />
          </>
        )}
        {canUpdateUserInfo && (
          <>
            <MenuItem
              onClick={() => {
                if (actionsMenuRow) {
                  setUpdatePhoneOrEmailUser({
                    customerId: Number(actionsMenuRow.customerId),
                    email: actionsMenuRow.email ?? '',
                    phoneNumber: actionsMenuRow.phoneNumber ?? '',
                  });
                  setUpdatePhoneOrEmailType('email');
                  setUpdatePhoneOrEmailModalOpen(true);
                }
                closeActionsMenu();
              }}
              sx={{ color: '#6366f1' }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}><EmailIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="E-mail Güncelle" primaryTypographyProps={{ fontWeight: 500 }} />
            </MenuItem>
            <Divider sx={{ my: 0.25 }} />
            <MenuItem
              onClick={() => {
                if (actionsMenuRow) {
                  setUpdatePhoneOrEmailUser({
                    customerId: Number(actionsMenuRow.customerId),
                    email: actionsMenuRow.email ?? '',
                    phoneNumber: actionsMenuRow.phoneNumber ?? '',
                  });
                  setUpdatePhoneOrEmailType('phone');
                  setUpdatePhoneOrEmailModalOpen(true);
                }
                closeActionsMenu();
              }}
              sx={{ color: '#0ea5e9' }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}><PhoneIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Cep No Güncelle" primaryTypographyProps={{ fontWeight: 500 }} />
            </MenuItem>
            <Divider sx={{ my: 0.25 }} />
          </>
        )}
        {canDeleteUser && (
          <MenuItem
            onClick={() => {
              if (actionsMenuRow) handleDeleteButtonClick(actionsMenuRow);
              closeActionsMenu();
            }}
            disabled={deleteUserLoading || isDeleteUserPermissionLoading}
            sx={{
              color: '#dc2626',
              '&:hover': {
                backgroundColor: 'rgba(220, 38, 38, 0.08)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Sil" primaryTypographyProps={{ fontWeight: 500 }} />
          </MenuItem>
        )}
      </Menu>

      {/* Rol Yönetimi Modal'ı */}
      <RoleManagementModal
        open={roleModalOpen}
        onClose={handleRoleModalClose}
        selectedUser={selectedUser}
        rolesData={rolesData}
        selectedRole={selectedRole}
        setSelectedRole={setSelectedRole}
        userCurrentRoles={userCurrentRoles}
        roleLoading={roleLoading}
        roleSuccess={roleSuccess}
        currentRoleLoading={currentRoleLoading}
        onRoleAssign={handleRoleAssign}
        extractRoleName={extractRoleName}
      />

      {/* Optimize edilmiş kullanıcı oluşturma modalı */}
      <OptimizedCreateUserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        onUserCreated={handleUserCreated}
      />

      {/* E-mail / Cep No güncelleme modalı */}
      <UpdatePhoneOrEmailModal
        open={updatePhoneOrEmailModalOpen}
        onClose={() => {
          setUpdatePhoneOrEmailModalOpen(false);
          setUpdatePhoneOrEmailUser(null);
        }}
        type={updatePhoneOrEmailType}
        user={updatePhoneOrEmailUser}
        onSuccess={handleUserCreated}
      />

      {/* Kullanıcı durumu güncelleme onay dialog'u */}

      <ConfirmationModal
        isOpen={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        onConfirm={handleStatusUpdate}
        title="Kullanıcı Durumunu Değiştir"
        confirmButtonText="Evet"
        closeButtonText="İptal"
        isLoading={statusUpdateLoading}
        customHeaderStyle={{
          background: selectedUserForStatus?.isActive ? '#f29c2e' : '#2cba83',
          titleColor: 'white',
          icon: (
            <Avatar
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                width: 32,
                height: 32,
              }}
            >
              <Warning fontSize="small" />
            </Avatar>
          ),
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          {selectedUserForStatus && (
            <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 2 }}>
              <>
                <strong>{selectedUserForStatus.userName}</strong> adlı kullanıcıyı{' '}
                <strong>{selectedUserForStatus.isActive ? 'pasif' : 'aktif'}</strong> etmek istediğinizden emin misiniz?
              </>
            </Typography>
          )}
        </Box>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={deleteConfirmDialogOpen}
        onClose={handleDeleteConfirmDialogClose}
        onConfirm={handleDeleteUser}
        title="Kullanıcıyı Sil"
        confirmButtonText="Sil"
        closeButtonText="İptal"
        isLoading={deleteUserLoading}
        customHeaderStyle={{
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
          titleColor: 'white',
          icon: (
            <Avatar
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                width: 32,
                height: 32,
              }}
            >
              <Warning fontSize="small" />
            </Avatar>
          ),
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          {selectedUserForDelete && (
            <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 2 }}>
              <strong>{selectedUserForDelete.userName}</strong> adlı kullanıcıyı silmek istediğinizden emin misiniz? Bu
              işlem geri alınamaz.
            </Typography>
          )}
        </Box>
      </ConfirmationModal>
    </div>
  );
};

export default UsersList;
